<script setup lang="ts">
import { useNewDivisionAccess } from '@/helpers/composables/useNewDivisionAccess'
import { $userActivationLinkCreate, $userOptions } from '@/api/users'
import { useSnackbarStore } from '@/store/snackbar'
import { requiredValidator } from '@validators'

interface IProps {
  modelValue?: boolean
  itemType?: 'branding'
  selectedItemIds?: number[]
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: false,
  itemType: 'branding',
  selectedItemIds: () => [],
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'onSubmit'): void
}>()

const snackbarStore = useSnackbarStore()
const { filterAllowedNewDivisionOptions, normalizedUserDivisions } = useNewDivisionAccess()

const shareForm = ref()
const loading = ref(false)
const optionsLoading = ref(false)

const activationLinkTtlHours = ref(24)
const activationEmails = ref<string[]>([])
const activationEmailInput = ref('')
const selectedCustomer = ref<any>(null)
const selectedSuppliers = ref<any[]>([])
const selectedCustomEventRole = ref<any>(null)
const user = ref<{
  user_role: number | null
  divisions: string[]
}>({
  user_role: 2,
  divisions: [],
})

const roles = ref<any[]>([])
const divisionOptions = ref<any[]>([])

const dialog = computed({
  get() {
    return props.modelValue
  },
  set(value: boolean) {
    emit('update:modelValue', value)
  },
})

const activationEmailCount = computed(() => {
  return normalizeActivationEmails(activationEmails.value).length
})

const normalizeActivationEmails = (value: string | string[]) => {
  const values = Array.isArray(value) ? value : [value]
  const emails = values
    .flatMap(item => String(item || '').split(/[\s,;]+/))
    .map(item => item.trim())
    .filter(Boolean)
  const seenEmails = new Set<string>()

  return emails.filter(email => {
    const emailKey = email.toLowerCase()

    if (seenEmails.has(emailKey)) {
      return false
    }

    seenEmails.add(emailKey)

    return true
  })
}

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

const activationEmailRules = [
  (value: string) => activationEmailCount.value > 0 || normalizeActivationEmails(value).length > 0 || 'At least one email is required',
  (value: string) => {
    const inputEmails = normalizeActivationEmails(value)
    const invalidEmail = inputEmails.find(email => !isValidEmail(email))

    return !invalidEmail || `Invalid email: ${invalidEmail}`
  },
]

const addActivationEmails = (value?: string) => {
  const emailsToAdd = normalizeActivationEmails(value || activationEmailInput.value)
  const invalidEmail = emailsToAdd.find(email => !isValidEmail(email))

  if (!emailsToAdd.length || invalidEmail) {
    return
  }

  activationEmails.value = normalizeActivationEmails([...activationEmails.value, ...emailsToAdd])
  activationEmailInput.value = ''
}

const removeActivationEmail = (email: string) => {
  activationEmails.value = activationEmails.value.filter(item => item.toLowerCase() !== email.toLowerCase())
}

const onActivationEmailPaste = (event: ClipboardEvent) => {
  const pastedText = event.clipboardData?.getData('text')

  if (!pastedText || normalizeActivationEmails(pastedText).length <= 1) {
    return
  }

  event.preventDefault()
  addActivationEmails(pastedText)
}

const resetForm = () => {
  activationLinkTtlHours.value = 24
  activationEmails.value = []
  activationEmailInput.value = ''
  selectedCustomer.value = null
  selectedSuppliers.value = []
  selectedCustomEventRole.value = null
  user.value = {
    user_role: 2,
    divisions: [],
  }
}

const getUserOptions = async () => {
  if (roles.value.length && divisionOptions.value.length) {
    return
  }

  optionsLoading.value = true
  try {
    const res = await $userOptions()
    roles.value = res.data.actions.POST.user_role.choices
    divisionOptions.value = filterAllowedNewDivisionOptions(
      res.data.actions.POST.divisions.choices,
      normalizedUserDivisions.value,
    )
  } catch (e) {
    console.log(e)
  } finally {
    optionsLoading.value = false
  }
}

const closeDialog = () => {
  dialog.value = false
}

const submit = async () => {
  if (activationEmailInput.value) {
    addActivationEmails()
  }

  const validation = await shareForm.value?.validate()
  if (!validation?.valid) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: 'Check all required fields'
    })
    return
  }

  if (!props.selectedItemIds.length) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: 'Select at least one item to share'
    })
    return
  }

  loading.value = true
  try {
    await $userActivationLinkCreate({
      emails: normalizeActivationEmails(activationEmails.value),
      expires_in_hours: Number(activationLinkTtlHours.value),
      user_role: user.value.user_role,
      customer: selectedCustomer.value ? selectedCustomer.value.id : null,
      divisions: user.value.divisions || [],
      suppliers: selectedSuppliers.value ? selectedSuppliers.value.map(item => item.id) : [],
      custom_event_role: selectedCustomEventRole.value ? selectedCustomEventRole.value.id : null,
      item_type: props.itemType,
      line_item_ids: props.selectedItemIds,
    })

    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: `Activation ${activationEmailCount.value === 1 ? 'link' : 'links'} created successfully!`
    })
    emit('onSubmit')
    closeDialog()
  } catch (e: any) {
    const mes = e.response?.data ? e.response.data : e.message
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${JSON.stringify(mes)}`
    })
  } finally {
    loading.value = false
  }
}

watch(dialog, async value => {
  if (value) {
    resetForm()
    await getUserOptions()
  }
})
</script>

<template>
  <VDialog
    v-model="dialog"
    max-width="860"
    persistent
  >
    <VCard>
      <VOverlay
        v-model="loading"
        persistent
        absolute
        contained
        class="align-center justify-center"
      >
        <VProgressCircular
          indeterminate
          color="primary"
        />
      </VOverlay>

      <VCardTitle>
        Share by Email
      </VCardTitle>

      <VCardText>
        <VForm
          ref="shareForm"
          @submit.prevent="submit"
        >
          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <label class="label-text">Customer</label>
              <CustomerSelect
                v-model="selectedCustomer"
                :label="''"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <label class="label-text">Divisions</label>
              <VSelect
                v-model="user.divisions"
                :items="[...divisionOptions]"
                multiple
                item-value="value"
                item-title="display_name"
                :loading="optionsLoading"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <label class="label-text">Supplier</label>
              <MultipleSuppliersSelect
                v-model="selectedSuppliers"
                :label="''"
                :view-all-selected-values="true"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <label class="label-text">TTL, hours *</label>
              <VTextField
                v-model.number="activationLinkTtlHours"
                :rules="[requiredValidator]"
                min="1"
                max="720"
                type="number"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <label class="label-text">User Role *</label>
              <VSelect
                v-model="user.user_role"
                :items="[...roles]"
                :loading="optionsLoading"
                :rules="[requiredValidator]"
                item-title="display_name"
                item-value="value"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <label class="label-text">Event Role</label>
              <CustomEventRoleSelect
                v-model="selectedCustomEventRole"
                :label="''"
              />
            </VCol>
            <VCol cols="12">
              <label class="label-text">Emails *</label>
              <VRow>
                <VCol
                  cols="12"
                  sm="10"
                >
                  <VTextField
                    v-model="activationEmailInput"
                    :rules="activationEmailRules"
                    name="emails"
                    type="email"
                    @keydown.enter.prevent="addActivationEmails()"
                    @blur="addActivationEmails()"
                    @paste="onActivationEmailPaste"
                  />
                </VCol>
                <VCol
                  cols="12"
                  sm="2"
                  class="d-flex align-start"
                >
                  <VBtn
                    class="w-100"
                    color="primary"
                    variant="tonal"
                    @click="addActivationEmails()"
                  >
                    <VIcon
                      start
                      icon="mdi-plus"
                    />
                    Add
                  </VBtn>
                </VCol>
                <VCol
                  v-if="activationEmails.length"
                  cols="12"
                  class="pt-0"
                >
                  <div class="d-flex flex-wrap ga-2">
                    <VChip
                      v-for="email in activationEmails"
                      :key="email"
                      closable
                      class="mx-1"
                      color="secondary"
                      variant="tonal"
                      @click:close="removeActivationEmail(email)"
                    >
                      {{ email }}
                    </VChip>
                  </div>
                </VCol>
              </VRow>
            </VCol>
          </VRow>
        </VForm>
      </VCardText>

      <VCardActions>
        <VSpacer />
        <VBtn
          variant="text"
          @click="closeDialog"
        >
          Cancel
        </VBtn>
        <VBtn
          color="primary"
          :loading="loading"
          :disabled="!selectedItemIds.length"
          @click="submit"
        >
          Send {{ activationEmailCount || '' }} Link{{ activationEmailCount === 1 ? '' : 's' }}
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
